import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatNumber, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, concatMap, filter, map, takeUntil } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Moment } from 'moment/moment';
import * as moment from 'moment';
import { AccountService } from '../../../core/services/account.service';
import { IInvoice, IInvoiceTariff, IReversRoll } from '../../../core/models/organisation.model';
import { AlertsService } from '../../../core/services/alerts.service';
import { UsersService } from '../../users/user.service';
import { TariffService } from '../../../core/services/tariff.service';
import { SharedModule } from '../../../shared/shared.module';
import { DatepickerModule } from '../../../shared/datepicker/datepicker.module';
import { PaymentAccountModalService } from '../payment-account-modal.service';
import { IOrganisation } from '../../../core/modules/organization/organization.types';
import { PaymentAccountApiService } from '../../../core/modules/payment-account/payment-account-api.service';
import { IInvoices, IIsmetAccess } from '../../../core/modules/payment-account/payment-account.types';

@Component({
  selector: 'tax-account',
  templateUrl: './payment-account-page.component.html',
  styleUrls: ['./payment-account-page.component.sass'],
  standalone: true,
  imports: [
    SharedModule,
    DatepickerModule,
    NgIf,
    NgFor,
    FormsModule,
  ],
})
export class PaymentAccountPageComponent implements OnInit, OnDestroy {
  // @ts-ignore
  balance: IBalance;
  // @ts-ignore
  organization: IOrganisation;
  reversRoll: Array<IReversRoll> = [];
  isRootUser: boolean = false;
  invoices: Array<IInvoice> = [];
  // @ts-ignore
  invoice: IInvoice;
  dateFrom: Moment = moment();
  dateTo: Moment = moment();
  visibility = {
    isSelectedInvoice: false,
    isSelectedReverseRoll: false,
    isShowDisabled: true,
    isShowMonthDisabled: true,
  };

  emptyListPlaceholder: string = 'account_payment.list.select.action';

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private alertsService: AlertsService,
    private accountService: AccountService,
    private paymentAccountApiService: PaymentAccountApiService,
    private usersService: UsersService,
    private tariffService: TariffService,
    private paymentAccountModalService: PaymentAccountModalService,
  ) { }

  ngOnInit(): void {
    this.dateFrom.subtract(1, 'month');
    this.usersService.getCurrentUserInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.isRootUser = user.permissions.includes('default.root');
        this.organization = user.organisation;
      });
    this.getBalance();
    this.showSumToActivateKkmsAlert();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  inputDate(event: MatDatepickerInputEvent<any, any>, type: string): void {
    this.visibility.isShowDisabled = false;
    type === 'dateTo' ? this.dateTo = event.value : this.dateFrom = event.value;
  }

  openInvoiceModal(invoice: IInvoice): void {
    this.invoice = invoice;
    this.accountService.getInvoice(invoice.invoiceId, invoice.invoiceDate)
      .pipe(
        map(res => Object.values(res.invoicesData)),
        takeUntil(this.destroy$),
      )
      .subscribe((response: Array<IInvoiceTariff>) => {
        this.invoice.invoicesData = response;
        this.paymentAccountModalService.openInvoiceModal(invoice);
      });
  }

  openPaymentMethodModal(billingAccountNumber: string): void {
    this.paymentAccountModalService.openPaymentMethodModal(billingAccountNumber);
  }

  showMonthlyOperations(): void {
    this.dateFrom = moment();
    this.dateTo = moment();
    this.dateFrom.subtract(1, 'months');
    this.visibility.isShowMonthDisabled = true;
    this.getOperations();
  }

  showOperations(): void {
    this.visibility.isShowDisabled = true;
    this.visibility.isShowMonthDisabled = false;
    this.getOperations();
  }

  getBalance(): void {
    this.paymentAccountApiService.fetchBalance()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.balance = response;
        this.showBalanceAlert();
      });
  }

  showReverseRoll(): void {
    this.visibility.isSelectedReverseRoll = true;
    this.visibility.isSelectedInvoice = !this.visibility.isSelectedReverseRoll;
    this.visibility.isShowDisabled = false;
  }

  showInvoice(): void {
    this.visibility.isSelectedInvoice = true;
    this.visibility.isSelectedReverseRoll = !this.visibility.isSelectedInvoice;
    this.visibility.isShowDisabled = false;
  }

  getTokenAndRedirect(): void {
    this.paymentAccountApiService.registerOrganisation()
      .pipe(
        concatMap(() => this.paymentAccountApiService.fetchIsmetToken()),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (response: IIsmetAccess) => {
          window.open(`https://ismet.kz/bin/ocp/redirect?token=${response.token}`, '_blank');
        },
      );
  }

  private getOperations(): void {
    if (!this.visibility.isSelectedReverseRoll) {
      this.accountService.getInvoices(this.dateTo.toDate(), this.dateFrom.toDate())
        .subscribe((response: IInvoices) => {
          this.invoices = response.invoices;
          this.emptyListPlaceholder = this.invoices.length ? '' : 'account_payment.list.empty';
          this.reversRoll.length = 0;
        });
    } else {
      this.accountService.getReverseRoll(this.dateTo.toDate(), this.dateFrom.toDate())
        .pipe(
          map(res => Object.values(res.rolls)),
          takeUntil(this.destroy$),
        )
        .subscribe((response: Array<IReversRoll>) => {
          this.reversRoll = response;
          this.emptyListPlaceholder = this.reversRoll.length ? '' : 'account_payment.list.empty';
          this.invoices.length = 0;
        });
    }
  }

  private showBalanceAlert(): void {
    const isShowAlert = (this.balance.clientStatus === 1)
      || (this.balance.amount < 0 && this.balance.clientStatus === 2);

    if (isShowAlert) {
      this.alertsService.add({
        message: 'account_payment.indebtedness.alert',
        messageVariables: {
          indebtednessSum: this.balance.amount,
        },
      });
    }
  }

  private showSumToActivateKkmsAlert(): void {
    this.tariffService.getRequiredSumToActivateDisabledKkms()
      .pipe(
        filter(sumToActivateKkms => sumToActivateKkms > 0),
        takeUntil(this.destroy$),
      )
      .subscribe((sumToActivateKkms) => {
        const formattedSumToActivateKkms = formatNumber(sumToActivateKkms, 'ru-KZ');
        this.alertsService.add({
          message: 'account_payment.indebtedness.kkms.disabled.alert',
          messageVariables: {
            paymentSum: formattedSumToActivateKkms,
          },
        });
      });
  }
}
